import { AfterViewInit, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { tap } from 'rxjs/operators';

import { InputTypes } from 'app/shared/enums/input-types.enum';
import { InputRequirements, PasswordRequirementsService } from 'app/shared/services/password-requirements.service';

@UntilDestroy()
@Component({
  selector: 'kh-password-requirements',
  templateUrl: './password-requirements.component.html',
  styleUrls: ['./password-requirements.component.less'],
  standalone: false
})
export class PasswordRequirementsComponent implements AfterViewInit {
  private readonly passwordRequirementsService = inject(PasswordRequirementsService);

  @Input() public passwordFormControl!: AbstractControl;
  @Input() public matchingFormControl!: AbstractControl;
  @Output() public pwdMatchEvent: EventEmitter<boolean> = new EventEmitter();
  @Output() public pwdComplexEvent: EventEmitter<boolean> = new EventEmitter();

  public inputTypes = InputTypes;
  public markAsError = false;
  public showRequired = false;
  public requirementMessages!: { [key in InputRequirements]: string };
  private candidatePassword = '';

  public ngAfterViewInit() {
    this.requirementMessages = this.passwordRequirementsService.getAllRequirementMessages();
    this.subscribeToFirstPasswordUpdates();
    this.subscribeToPasswordMatcherUpdates();
  }

  private subscribeToFirstPasswordUpdates(): void {
    this.passwordFormControl.valueChanges.pipe(
      tap((pwd) => this.validateCandidatePassword(pwd)),
      tap(() => this.pwdComplexEvent.emit(this.isPasswordComplex())),
      untilDestroyed(this)
    ).subscribe();
  }

  private subscribeToPasswordMatcherUpdates(): void {
    if (this.matchingFormControl) {
      this.matchingFormControl.valueChanges.pipe(
        tap((secondPwd) => this.updatePasswordMatchValidation(secondPwd)),
        untilDestroyed(this)
      ).subscribe();
    }
  }

  private validateCandidatePassword(password: string): void {
    if (password && password.length > 0) {
      this.candidatePassword = password;
      this.validatePassword();
    }
    else {
      this.candidatePassword = '';
      this.passwordRequirementsService.resetAllRequirements();
    }
  }

  private updatePasswordMatchValidation(secondPwd: string): void {
    if (this.candidatePassword === secondPwd) {
      this.pwdMatchEvent.emit(true);
    }
    else {
      this.pwdMatchEvent.emit(false);
    }
  }

  public getRequirementMessage(requirement: InputRequirements): string {
    return this.passwordRequirementsService.getSingleRequirementMessage(requirement);
  }

  public getRequirementKeys(): InputRequirements[] {
    return this.passwordRequirementsService.getAllRequirementKeys();
  }

  public isPasswordComplex(): boolean {
    if (!this.getInputSoFar()) {
      this.showRequired = true;

      return false;
    }
    this.showRequired = false;
    this.markAsError = true;
    this.validatePassword();

    return this.passwordRequirementsService.isAllRequirementsMet();
  }

  public isRequirementUnmetMessageShowable(key: InputRequirements): boolean {
    return !this.isRequirementMet(key) && this.markAsError;
  }

  public isRequirementMet(requirement: InputRequirements): boolean {
    return this.passwordRequirementsService.isRequirementMet(requirement) || false;
  }

  public showRequirements(): boolean {
    return this.getInputSoFar().length > 0;
  }

  public getRequirementMessageByKey(key: InputRequirements): string {
    return this.requirementMessages[key];
  }

  private validatePassword() {
    this.showRequired = false;
    this.passwordRequirementsService.updateCandidate(this.getInputSoFar());
  }

  public getInputSoFar(): string {
    return (this.candidatePassword) ? this.candidatePassword : '';
  }
}
